import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchWardBeds, AddBed } from '../../../helper/BedHelper';
import '../HospitalDashboard.css';
import { useLocation, useNavigate } from 'react-router-dom';
import AddBedModal from './AddBedModal';
import moment from 'moment';

const displayValue = (value) => (value == null || value === '' ? '—' : value);

const formatDateTime = (value) => {
  if (!value) return '—';
  return moment(value).format('DD-MM-YYYY HH:mm');
};

const SuccessToast = ({ title, description }) => (
  <div className="hospital-toast" role="status" aria-live="polite">
    <span className="hospital-toast-icon" aria-hidden="true">✓</span>
    <div className="hospital-toast-text">
      <strong>{title}</strong>
      <span>{description}</span>
    </div>
  </div>
);

const BedCard = ({ bed, onClick }) => (
  <article
    className="hospital-card"
    onClick={() => onClick(bed)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick(bed);
      }
    }}
    role="button"
    tabIndex={0}
  >
    <div className="hospital-card-head">
      <h3 className="hospital-card-name">{displayValue(bed.name)}</h3>
      <span className={`hospital-status-badge ${bed.isActive ? 'hospital-status-badge--active' : 'hospital-status-badge--inactive'}`}>
        {bed.isActive ? 'Active' : 'Inactive'}
      </span>
    </div>

    <div className="hospital-card-details">
      <div className="hospital-card-detail-row">
        <span className="hospital-card-detail-label">ID</span>
        <span className="hospital-card-detail-val">{displayValue(bed.id)}</span>
      </div>
      <div className="hospital-card-detail-row">
        <span className="hospital-card-detail-label">CREATED</span>
        <span className="hospital-card-detail-val">{formatDateTime(bed.createdAt)}</span>
      </div>
      <div className="hospital-card-detail-row">
        <span className="hospital-card-detail-label">UPDATED</span>
        <span className="hospital-card-detail-val">{formatDateTime(bed.updatedAt)}</span>
      </div>
    </div>

    <div className="hospital-card-foot">
      <span>View machines</span>
      <span aria-hidden="true">›</span>
    </div>
  </article>
);

const BedDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const queryParams = new URLSearchParams(location.search);
  const wardId = queryParams.get('wardId');

  const [hospital, setHospital] = useState({});
  const [ward, setWard] = useState({});
  const [beds, setBeds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const loadBeds = () => {
    if (!wardId) return;
    fetchWardBeds(wardId)
      .then((res) => {
        setBeds(Array.isArray(res.data) ? res.data : []);
        setWard(res.ward || {});
        setHospital(res.hospital || {});
      })
      .catch((err) => console.error('Error fetching beds:', err));
  };

  useEffect(() => {
    loadBeds();
  }, [wardId]);

  useEffect(() => {
    if (showSuccessToast) {
      loadBeds();
    }
  }, [showSuccessToast]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.metaKey && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleAddBed = (newBed) => {
    AddBed(newBed)
      .then(() => {
        setIsModalOpen(false);
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
      })
      .catch((err) => {
        console.error('Error adding bed:', err);
        alert(`Error adding bed: ${err}`);
      });
  };

  const filteredBeds = useMemo(() => (
    (Array.isArray(beds) ? beds : []).filter((bed) => (
      bed?.name?.toLowerCase().includes(searchQuery.toLowerCase())
    ))
  ), [beds, searchQuery]);

  const handleRowClick = (record) => {
    navigate(`/hospital-details/devices?bedId=${record.id}`);
  };

  return (
    <div className="hospital-page">
      {showSuccessToast && (
        <SuccessToast
          title="Bed added successfully"
          description="The bed has been added to this ward."
        />
      )}

      <nav className="hospital-breadcrumb" aria-label="Breadcrumb">
        <button type="button" onClick={() => navigate(`/hospital-details/wards?hospitalId=${hospital?.id}`)}>
          {displayValue(hospital?.name)}
        </button>
        <span className="hospital-breadcrumb-sep">›</span>
        <span>{displayValue(ward?.name)}</span>
        <span className="hospital-breadcrumb-sep">›</span>
        <span>Beds</span>
      </nav>

      <div className="hospital-header">
        <h3 className="hospital-title">Beds</h3>
      </div>

      <div className="hospital-filters-bar">
        <input
          ref={searchInputRef}
          type="text"
          className="hospital-search-input"
          placeholder="Search bed name..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
        <button
          type="button"
          className="hospital-btn hospital-btn--primary"
          onClick={() => setIsModalOpen(true)}
        >
          Add Bed
        </button>
      </div>

      <div className="hospital-stats-grid">
        <div className="hospital-stat-card hospital-stat-card--total">
          <span className="hospital-stat-label">TOTAL BEDS</span>
          <span className="hospital-stat-value">{filteredBeds.length}</span>
        </div>
      </div>

      <div className="hospital-toolbar">
        <h2 className="hospital-section-title">Bed List</h2>
      </div>

      <div className="hospital-cards-scroll">
        {filteredBeds.length === 0 ? (
          <div className="hospital-empty">No beds found.</div>
        ) : (
          <div className="hospital-cards-grid">
            {filteredBeds.map((bed) => (
              <BedCard key={bed.id} bed={bed} onClick={handleRowClick} />
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (
        <AddBedModal
          onClose={() => setIsModalOpen(false)}
          onSave={handleAddBed}
          wardId={wardId}
          wardName={ward.name}
          hospitalName={hospital.name}
        />
      )}
    </div>
  );
};

export default BedDashboard;
